// FIN-11812 (Phase C.2 GREEN) — small badge rendered next to a typology
// header or variant row on the Typologies tab to indicate whether the
// last scenario-validate run (saga FIN-11800 / FIN-11810) found the row
// valid, invalid, or hasn't yet validated it.
//
// FIN-13484: a third state, `warning` (amber) — the row passed validation but
// /validate returned advisory findings for it (e.g. a cross-type Group By key
// left without a Perspective). Never blocks save or launch. An `invalid` row
// that also carries warnings lists them after the errors in its tooltip.
//
// Extracted from typologies.js so the visual contract is unit-testable
// without mounting the (heavy) Typologies tab — same standalone-helper
// pattern used for buildSaveTypologyPayload + sortCatalogForPicker.
//
// Accessibility: the badge's aria-label carries the failure reason
// directly (e.g. "invalid: bad grouping key, count_distinct collision")
// so screen readers convey it without depending on a Tooltip overlay
// (which AntD renders into a portal — out of the static a11y tree).
// A visual Tooltip is layered on top for sighted users.
import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleTwoTone
} from '@ant-design/icons';

const VariantStatusBadge = ({ status, messages, warnings }) => {
  const hasWarnings = warnings && warnings.length > 0;
  const effective = status === 'valid' && hasWarnings ? 'warning' : status;
  if (
    effective !== 'valid' &&
    effective !== 'invalid' &&
    effective !== 'warning'
  ) {
    return null;
  }
  if (effective === 'valid') {
    return (
      <CheckCircleTwoTone
        twoToneColor="#52c41a"
        aria-label="valid"
        role="img"
        style={{ marginLeft: 6 }}
      />
    );
  }
  if (effective === 'warning') {
    const joinedWarnings = hasWarnings ? warnings.join(', ') : '';
    return (
      <Tooltip title={joinedWarnings || 'Validation warning'}>
        <ExclamationCircleTwoTone
          twoToneColor="#faad14"
          aria-label={joinedWarnings ? `warning: ${joinedWarnings}` : 'warning'}
          role="img"
          style={{ marginLeft: 6 }}
        />
      </Tooltip>
    );
  }
  const joined = messages && messages.length > 0 ? messages.join(', ') : '';
  const label = joined ? `invalid: ${joined}` : 'invalid';
  const tooltip = [
    joined || 'Validation failed',
    hasWarnings ? `Warnings: ${warnings.join(', ')}` : ''
  ]
    .filter(Boolean)
    .join(' — ');
  return (
    <Tooltip title={tooltip}>
      <CloseCircleTwoTone
        twoToneColor="#ff4d4f"
        aria-label={label}
        role="img"
        style={{ marginLeft: 6 }}
      />
    </Tooltip>
  );
};

VariantStatusBadge.propTypes = {
  status: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.string),
  warnings: PropTypes.arrayOf(PropTypes.string)
};

VariantStatusBadge.defaultProps = {
  status: undefined,
  messages: [],
  warnings: []
};

export default VariantStatusBadge;
